/**
 * Training profile tutorial bulk import modal.
 * Provides JSON template generation, AI-oriented schema guidance, nested quest/step validation,
 * and batch import workflows for tutorial quests with spotlight metadata.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Input,
  message,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AimOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  CopyOutlined,
  DownloadOutlined,
  FormatPainterOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import Editor, { loader } from '@monaco-editor/react';
import trainingProfilesService from '../../services/trainingProfilesService';
import { AVAILABLE_QUEST_ICONS } from '../Simulations/HeatPumpSimulation/questIcons';
import { AVAILABLE_SPOTLIGHT_SELECTORS } from '../Simulations/HeatPumpSimulation/spotlightTargets';
import styles from './TrainingProfileBulkImportModal.module.css';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const MONACO_INIT_TIMEOUT_MS = 10000;
const MONACO_MOUNT_TIMEOUT_MS = 10000;

const QUEST_CONSTRAINTS = Object.freeze({
  titleMaxLength: 255,
  categoryMaxLength: 100,
  difficultyMaxLength: 50,
  estimatedTimeMaxLength: 50,
  iconKeyMaxLength: 100,
  richContentMaxLength: 5000,
  stepKeyMaxLength: 50,
  stepTitleMaxLength: 255,
});

const DEFAULT_CONDITION = Object.freeze({
  combinator: 'and',
  rules: [
    {
      field: 'outdoorTemperature',
      operator: 'lessThanOrEqual',
      value: -10,
      label: 'Outdoor temperature <= -10C',
    },
  ],
});

const CROSS_FIELD_CONDITION = Object.freeze({
  combinator: 'and',
  rules: [
    {
      field: 'supplyTemperature',
      operator: 'greaterThanOrEqualFieldMultiplier',
      compareField: 'outdoorTemperature',
      multiplier: -2,
      label: 'Supply temperature is at least 2x the absolute outdoor temperature',
    },
  ],
});

const CONDITION_FIELD_DEFINITIONS = Object.freeze([
  { key: 'isSimulationRunning', type: 'boolean' },
  { key: 'outdoorTemperature', type: 'number' },
  { key: 'selectedRoomType', type: 'string' },
  { key: 'desiredTemperature', type: 'number' },
  { key: 'supplyTemperature', type: 'number' },
  { key: 'heatOutput', type: 'number' },
  { key: 'qNet', type: 'number' },
  { key: 'envelopeLoss', type: 'number' },
  { key: 'ventilationLoss', type: 'number' },
  { key: 'totalHeatLoss', type: 'number' },
  { key: 'airChangeRate', type: 'number' },
  { key: 'currentCOP', type: 'number' },
  { key: 'currentTemperature', type: 'number' },
  { key: 'valvePosition', type: 'number' },
  { key: 'thermostatLevel', type: 'number' },
  { key: 'radiatorType', type: 'string' },
  { key: 'radiatorCount', type: 'number' },
  { key: 'systemPressure', type: 'number' },
  { key: 'uValue', type: 'number' },
  { key: 'totalArea', type: 'number' },
  { key: 'roofArea', type: 'number' },
  { key: 'wallArea', type: 'number' },
  { key: 'windowArea', type: 'number' },
  { key: 'selectedCity', type: 'string' },
]);

const CONDITION_FIELD_SET = new Set(CONDITION_FIELD_DEFINITIONS.map((field) => field.key));
const NUMERIC_CONDITION_FIELDS = new Set(
  CONDITION_FIELD_DEFINITIONS.filter((field) => field.type === 'number').map((field) => field.key)
);
const BOOLEAN_CONDITION_FIELDS = new Set(
  CONDITION_FIELD_DEFINITIONS.filter((field) => field.type === 'boolean').map((field) => field.key)
);
const CONDITION_OPERATOR_SET = new Set([
  'equals',
  'notEquals',
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
  'between',
  'in',
  'contains',
  'approximately',
  'greaterThanOrEqualFieldMultiplier',
  'lessThanOrEqualFieldMultiplier',
]);
const SPOTLIGHT_POSITION_SET = new Set(['top', 'bottom', 'left', 'right']);
const KNOWN_ICON_KEYS = new Set(AVAILABLE_QUEST_ICONS.map((icon) => icon.key));
const ICON_LABEL_MAP = new Map(AVAILABLE_QUEST_ICONS.map((icon) => [icon.key, icon.label]));

const getFirstDefined = (source, keys) => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
      return source[key];
    }
  }

  return undefined;
};

const normalizeOptionalString = (value) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeOptionalInteger = (value) => {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : undefined;
  }

  return undefined;
};

const normalizeStep = (step) => ({
  orderIndex: normalizeOptionalInteger(getFirstDefined(step, ['OrderIndex', 'orderIndex'])),
  stepKey: normalizeOptionalString(getFirstDefined(step, ['StepKey', 'stepKey'])),
  taskTitle: normalizeOptionalString(getFirstDefined(step, ['TaskTitle', 'taskTitle'])),
  instructionRichContent: getFirstDefined(step, ['InstructionRichContent', 'instructionRichContent']),
  detailedGuidanceRichContent: getFirstDefined(step, ['DetailedGuidanceRichContent', 'detailedGuidanceRichContent']),
  successIndicatorRichContent: getFirstDefined(step, ['SuccessIndicatorRichContent', 'successIndicatorRichContent']),
  spotlightTarget: getFirstDefined(step, ['SpotlightTarget', 'spotlightTarget']),
});

const normalizeQuest = (quest) => {
  const rawSteps = getFirstDefined(quest, ['Steps', 'steps']);

  return {
    orderIndex: normalizeOptionalInteger(getFirstDefined(quest, ['OrderIndex', 'orderIndex'])),
    title: normalizeOptionalString(getFirstDefined(quest, ['Title', 'title'])),
    category: normalizeOptionalString(getFirstDefined(quest, ['Category', 'category'])),
    difficulty: normalizeOptionalString(getFirstDefined(quest, ['Difficulty', 'difficulty'])),
    estimatedTime: normalizeOptionalString(getFirstDefined(quest, ['EstimatedTime', 'estimatedTime'])),
    iconKey: normalizeOptionalString(getFirstDefined(quest, ['IconKey', 'iconKey'])),
    isChallenge: Boolean(getFirstDefined(quest, ['IsChallenge', 'isChallenge'])),
    learningObjectivesRichContent: getFirstDefined(quest, ['LearningObjectivesRichContent', 'learningObjectivesRichContent']),
    scenarioRichContent: getFirstDefined(quest, ['ScenarioRichContent', 'scenarioRichContent']),
    realWorldConnectionRichContent: getFirstDefined(quest, ['RealWorldConnectionRichContent', 'realWorldConnectionRichContent']),
    completionDescriptionRichContent: getFirstDefined(quest, ['CompletionDescriptionRichContent', 'completionDescriptionRichContent']),
    knowledgeSynthesisRichContent: getFirstDefined(quest, ['KnowledgeSynthesisRichContent', 'knowledgeSynthesisRichContent']),
    practicalTipRichContent: getFirstDefined(quest, ['PracticalTipRichContent', 'practicalTipRichContent']),
    conditionAst: getFirstDefined(quest, ['ConditionAst', 'conditionAst']),
    steps: Array.isArray(rawSteps) ? rawSteps.map(normalizeStep) : [],
  };
};

const mapNullableString = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const mapStepForRequest = (step, index) => ({
  orderIndex: Number.isInteger(step.orderIndex) ? step.orderIndex : index,
  stepKey: mapNullableString(step.stepKey),
  taskTitle: step.taskTitle || '',
  instructionRichContent: mapNullableString(step.instructionRichContent),
  detailedGuidanceRichContent: mapNullableString(step.detailedGuidanceRichContent),
  successIndicatorRichContent: mapNullableString(step.successIndicatorRichContent),
  spotlightTarget: step.spotlightTarget ?? null,
});

const mapQuestForRequest = (quest, index) => ({
  orderIndex: Number.isInteger(quest.orderIndex) ? quest.orderIndex : index,
  title: quest.title || '',
  category: mapNullableString(quest.category),
  difficulty: mapNullableString(quest.difficulty),
  estimatedTime: mapNullableString(quest.estimatedTime),
  iconKey: mapNullableString(quest.iconKey),
  isChallenge: Boolean(quest.isChallenge),
  learningObjectivesRichContent: mapNullableString(quest.learningObjectivesRichContent),
  scenarioRichContent: mapNullableString(quest.scenarioRichContent),
  realWorldConnectionRichContent: mapNullableString(quest.realWorldConnectionRichContent),
  completionDescriptionRichContent: mapNullableString(quest.completionDescriptionRichContent),
  knowledgeSynthesisRichContent: mapNullableString(quest.knowledgeSynthesisRichContent),
  practicalTipRichContent: mapNullableString(quest.practicalTipRichContent),
  conditionAst: quest.conditionAst ?? null,
  steps: quest.steps.map((step, stepIndex) => mapStepForRequest(step, stepIndex)),
});

const validateJsonTree = (value, label, maxDepth, maxCount) => {
  if (value === null || value === undefined) {
    return null;
  }

  let count = 0;

  const visit = (node, depth) => {
    if (depth > maxDepth) {
      return `${label} exceeds maximum nesting depth of ${maxDepth}.`;
    }

    if (Array.isArray(node)) {
      for (const item of node) {
        count += 1;
        if (count > maxCount) {
          return `${label} exceeds maximum item count of ${maxCount}.`;
        }
        const error = visit(item, depth + 1);
        if (error) {
          return error;
        }
      }
      return null;
    }

    if (node && typeof node === 'object') {
      for (const propertyValue of Object.values(node)) {
        count += 1;
        if (count > maxCount) {
          return `${label} exceeds maximum property count of ${maxCount}.`;
        }
        const error = visit(propertyValue, depth + 1);
        if (error) {
          return error;
        }
      }
    }

    return null;
  };

  return visit(value, 0);
};

const validateConditionValue = (field, operator, value) => {
  if (operator === 'between') {
    if (!Array.isArray(value) || value.length !== 2) {
      return 'The between operator requires a two-item value array.';
    }
    if (NUMERIC_CONDITION_FIELDS.has(field) && value.some((item) => typeof item !== 'number')) {
      return `The between operator for "${field}" requires numeric values.`;
    }
    return null;
  }

  if (operator === 'in') {
    if (!Array.isArray(value)) {
      return 'The in operator requires an array value.';
    }
    return null;
  }

  if (NUMERIC_CONDITION_FIELDS.has(field) && typeof value !== 'number') {
    return `Condition field "${field}" requires a numeric value.`;
  }

  if (BOOLEAN_CONDITION_FIELDS.has(field) && typeof value !== 'boolean') {
    return `Condition field "${field}" requires a boolean value.`;
  }

  if (!NUMERIC_CONDITION_FIELDS.has(field) && !BOOLEAN_CONDITION_FIELDS.has(field)) {
    const isValidType = typeof value === 'string' || Array.isArray(value);
    if (!isValidType) {
      return `Condition field "${field}" requires a string or array value.`;
    }
  }

  return null;
};

const validateConditionNode = (node, depth, stats) => {
  if (depth > 8) {
    return 'Condition AST exceeds maximum nesting depth of 8.';
  }

  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return 'Condition AST nodes must be objects.';
  }

  if (Object.prototype.hasOwnProperty.call(node, 'rules')) {
    const combinator = typeof node.combinator === 'string'
      ? node.combinator
      : (typeof node.condition === 'string' ? node.condition : 'and');

    if (!['and', 'or'].includes(String(combinator).toLowerCase())) {
      return 'Condition groups must use combinator "and" or "or".';
    }

    if (!Array.isArray(node.rules)) {
      return 'Condition group rules must be an array.';
    }

    for (const child of node.rules) {
      const error = validateConditionNode(child, depth + 1, stats);
      if (error) {
        return error;
      }
    }

    return null;
  }

  stats.ruleCount += 1;
  if (stats.ruleCount > 100) {
    return 'Condition AST cannot contain more than 100 rules.';
  }

  const field = normalizeOptionalString(node.field);
  if (!field || !CONDITION_FIELD_SET.has(field)) {
    return `Condition field "${field || '<missing>'}" is not allowed.`;
  }

  const operator = normalizeOptionalString(node.operator);
  if (!operator || !CONDITION_OPERATOR_SET.has(operator)) {
    return `Condition operator "${operator || '<missing>'}" is not allowed.`;
  }

  if (operator.includes('FieldMultiplier')) {
    const compareField = normalizeOptionalString(node.compareField);
    if (!compareField || !CONDITION_FIELD_SET.has(compareField) || !NUMERIC_CONDITION_FIELDS.has(compareField)) {
      return 'Cross-field multiplier conditions require an allowed numeric compareField.';
    }
    if (typeof node.multiplier !== 'number') {
      return 'Cross-field multiplier conditions require a numeric multiplier.';
    }
    return null;
  }

  if (!Object.prototype.hasOwnProperty.call(node, 'value')) {
    return 'Condition rule requires a value.';
  }

  return validateConditionValue(field, operator, node.value);
};

const validateConditionAst = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const stats = { ruleCount: 0 };
  return validateConditionNode(value, 0, stats);
};

const validateSpotlightTarget = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const treeError = validateJsonTree(value, 'Spotlight target', 4, 25);
  if (treeError) {
    return treeError;
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return 'Spotlight target must be a JSON object.';
  }

  if (value.position !== undefined) {
    if (typeof value.position !== 'string' || !SPOTLIGHT_POSITION_SET.has(value.position)) {
      return 'Spotlight target position must be one of: top, bottom, left, right.';
    }
  }

  if (value.selector !== undefined && typeof value.selector !== 'string') {
    return 'Spotlight target selector must be a string when provided.';
  }

  if (value.displayName !== undefined && typeof value.displayName !== 'string') {
    return 'Spotlight target displayName must be a string when provided.';
  }

  if (value.fallbackRect !== undefined) {
    if (!value.fallbackRect || typeof value.fallbackRect !== 'object' || Array.isArray(value.fallbackRect)) {
      return 'Spotlight target fallbackRect must be an object when provided.';
    }
  }

  return null;
};

const validateRichContentLength = (value, label) => {
  if (typeof value !== 'string') {
    return null;
  }

  if (value.trim().length > QUEST_CONSTRAINTS.richContentMaxLength) {
    return `${label} exceeds ${QUEST_CONSTRAINTS.richContentMaxLength} chars.`;
  }

  return null;
};

const validateStep = (step, stepIndex) => {
  const errors = [];

  if (!step.taskTitle) {
    errors.push('TaskTitle is required.');
  } else if (step.taskTitle.length > QUEST_CONSTRAINTS.stepTitleMaxLength) {
    errors.push(`TaskTitle exceeds ${QUEST_CONSTRAINTS.stepTitleMaxLength} chars.`);
  }

  if (step.stepKey && step.stepKey.length > QUEST_CONSTRAINTS.stepKeyMaxLength) {
    errors.push(`StepKey exceeds ${QUEST_CONSTRAINTS.stepKeyMaxLength} chars.`);
  }

  const contentError = validateRichContentLength(step.instructionRichContent, 'InstructionRichContent')
    || validateRichContentLength(step.detailedGuidanceRichContent, 'DetailedGuidanceRichContent')
    || validateRichContentLength(step.successIndicatorRichContent, 'SuccessIndicatorRichContent');

  if (contentError) {
    errors.push(contentError);
  }

  const spotlightError = validateSpotlightTarget(step.spotlightTarget);
  if (spotlightError) {
    errors.push(spotlightError);
  }

  return errors.map((error) => `Step ${stepIndex + 1}: ${error}`);
};

const validateQuest = (quest) => {
  const errors = [];

  if (!quest.title) {
    errors.push('Title is required.');
  } else if (quest.title.length > QUEST_CONSTRAINTS.titleMaxLength) {
    errors.push(`Title exceeds ${QUEST_CONSTRAINTS.titleMaxLength} chars.`);
  }

  if (quest.category && quest.category.length > QUEST_CONSTRAINTS.categoryMaxLength) {
    errors.push(`Category exceeds ${QUEST_CONSTRAINTS.categoryMaxLength} chars.`);
  }

  if (quest.difficulty && quest.difficulty.length > QUEST_CONSTRAINTS.difficultyMaxLength) {
    errors.push(`Difficulty exceeds ${QUEST_CONSTRAINTS.difficultyMaxLength} chars.`);
  }

  if (quest.estimatedTime && quest.estimatedTime.length > QUEST_CONSTRAINTS.estimatedTimeMaxLength) {
    errors.push(`EstimatedTime exceeds ${QUEST_CONSTRAINTS.estimatedTimeMaxLength} chars.`);
  }

  if (quest.iconKey && quest.iconKey.length > QUEST_CONSTRAINTS.iconKeyMaxLength) {
    errors.push(`IconKey exceeds ${QUEST_CONSTRAINTS.iconKeyMaxLength} chars.`);
  }

  const contentError = validateRichContentLength(quest.learningObjectivesRichContent, 'LearningObjectivesRichContent')
    || validateRichContentLength(quest.scenarioRichContent, 'ScenarioRichContent')
    || validateRichContentLength(quest.realWorldConnectionRichContent, 'RealWorldConnectionRichContent')
    || validateRichContentLength(quest.completionDescriptionRichContent, 'CompletionDescriptionRichContent')
    || validateRichContentLength(quest.knowledgeSynthesisRichContent, 'KnowledgeSynthesisRichContent')
    || validateRichContentLength(quest.practicalTipRichContent, 'PracticalTipRichContent');

  if (contentError) {
    errors.push(contentError);
  }

  const conditionError = validateConditionAst(quest.conditionAst);
  if (conditionError) {
    errors.push(conditionError);
  }

  quest.steps.forEach((step, stepIndex) => {
    errors.push(...validateStep(step, stepIndex));
  });

  return errors;
};

const parseJson = (jsonString) => {
  if (!jsonString || !jsonString.trim()) {
    return { success: false, error: 'Empty input', data: null, lineNumber: null };
  }

  try {
    const data = JSON.parse(jsonString);
    if (!Array.isArray(data)) {
      return { success: false, error: 'Root must be an array of quests', data: null, lineNumber: null };
    }
    return { success: true, error: null, data, lineNumber: null };
  } catch (error) {
    const match = error.message.match(/position (\d+)/i);
    let lineNumber = null;
    if (match) {
      const position = parseInt(match[1], 10);
      lineNumber = jsonString.substring(0, position).split('\n').length;
    }
    return { success: false, error: error.message, data: null, lineNumber };
  }
};

const findQuestLineRange = (jsonString, questIndex) => {
  try {
    let inString = false;
    let escapeNext = false;
    let inRootArray = false;
    let objectDepth = 0;
    let arrayDepth = 0;
    let currentQuestIndex = -1;
    let questStartLine = -1;
    let lineNumber = 1;

    for (let i = 0; i < jsonString.length; i += 1) {
      const char = jsonString[i];

      if (char === '\n') {
        lineNumber += 1;
        continue;
      }

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\' && inString) {
        escapeNext = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        continue;
      }

      if (inString) {
        continue;
      }

      if (char === '[') {
        if (arrayDepth === 0 && objectDepth === 0) {
          inRootArray = true;
        }
        arrayDepth += 1;
        continue;
      }

      if (char === ']') {
        arrayDepth -= 1;
        continue;
      }

      if (char === '{') {
        if (inRootArray && objectDepth === 0) {
          currentQuestIndex += 1;
          if (currentQuestIndex === questIndex) {
            questStartLine = lineNumber;
          }
        }
        objectDepth += 1;
        continue;
      }

      if (char === '}') {
        objectDepth -= 1;
        if (inRootArray && objectDepth === 0 && currentQuestIndex === questIndex) {
          return { startLine: questStartLine, endLine: lineNumber };
        }
      }
    }

    return null;
  } catch {
    return null;
  }
};

const generateTemplateJson = () => {
  const iconKeys = AVAILABLE_QUEST_ICONS.slice(0, 3).map((icon) => icon.key);
  const selectors = AVAILABLE_SPOTLIGHT_SELECTORS.slice(0, 4);

  const template = [
    {
      Title: 'Cold-weather startup check',
      Category: 'Commissioning',
      Difficulty: 'Beginner',
      EstimatedTime: '8 minutes',
      IconKey: iconKeys[0] || 'aim',
      IsChallenge: false,
      LearningObjectivesRichContent: '<p>Identify the main controls involved in starting the simulation and reading the first KPIs.</p>',
      ScenarioRichContent: '<p>The learner is preparing a heat pump training rig for its first cold-weather run.</p>',
      RealWorldConnectionRichContent: '<p>This mirrors the first walkthrough a field trainer gives before handing a system over to a junior installer.</p>',
      CompletionDescriptionRichContent: '<p>Complete the startup checks and verify the first stable readings before continuing.</p>',
      KnowledgeSynthesisRichContent: '<p>Startup is not only about pressing play. It is about confirming the resulting state.</p>',
      PracticalTipRichContent: '<p>Call out the metric to watch before the learner touches the control. This reduces guessing.</p>',
      ConditionAst: DEFAULT_CONDITION,
      Steps: [
        {
          StepKey: 'startup-1',
          TaskTitle: 'Start the simulation',
          InstructionRichContent: '<p>Use the run button to begin the simulation.</p>',
          DetailedGuidanceRichContent: '<p>Explain why the learner should start the simulation before interpreting any live readings.</p>',
          SuccessIndicatorRichContent: '<p>The simulation is running and the key values begin updating.</p>',
          SpotlightTarget: {
            selector: selectors[0]?.selector,
            position: 'bottom',
            displayName: selectors[0]?.name || 'Run button',
          },
        },
        {
          StepKey: 'startup-2',
          TaskTitle: 'Read the COP display',
          InstructionRichContent: '<p>Locate the COP widget and state the current value.</p>',
          DetailedGuidanceRichContent: '<p>Prompt the learner to connect the displayed COP with operating conditions instead of reading it as an isolated number.</p>',
          SuccessIndicatorRichContent: '<p>The learner identifies the COP display and reports the live value.</p>',
          SpotlightTarget: {
            selector: selectors[1]?.selector,
            position: 'right',
            displayName: selectors[1]?.name || 'COP display',
          },
        },
      ],
    },
    {
      Title: 'Supply temperature tuning challenge',
      Category: 'Optimization',
      Difficulty: 'Intermediate',
      EstimatedTime: '12 minutes',
      IconKey: iconKeys[1] || 'tool',
      IsChallenge: true,
      LearningObjectivesRichContent: '<p>Adjust a control, observe COP impact, and relate the outcome to outdoor temperature.</p>',
      ScenarioRichContent: '<p>The learner has to fine-tune supply temperature to keep the system efficient during a cold snap.</p>',
      RealWorldConnectionRichContent: '<p>This reflects common service visits where installers overcompensate with higher supply temperatures.</p>',
      CompletionDescriptionRichContent: '<p>Reach a stable supply temperature decision and justify it with observed data.</p>',
      KnowledgeSynthesisRichContent: '<p>Higher supply temperatures can solve comfort issues quickly but often reduce efficiency.</p>',
      PracticalTipRichContent: '<p>Ask for a justification after every control change so the learner practices reasoning, not trial-and-error.</p>',
      ConditionAst: CROSS_FIELD_CONDITION,
      Steps: [
        {
          StepKey: 'challenge-1',
          TaskTitle: 'Open the supply temperature control',
          InstructionRichContent: '<p>Find the supply temperature input in the control panel.</p>',
          DetailedGuidanceRichContent: '<p>Frame this as the first diagnostic lever the learner can test against the current outdoor conditions.</p>',
          SuccessIndicatorRichContent: '<p>The learner can point to the supply temperature control.</p>',
          SpotlightTarget: {
            selector: selectors[2]?.selector,
            position: 'left',
            displayName: selectors[2]?.name || 'Supply temperature control',
          },
        },
        {
          StepKey: 'challenge-2',
          TaskTitle: 'Check outdoor temperature context',
          InstructionRichContent: '<p>Look at the outdoor temperature before finalizing the supply temperature target.</p>',
          DetailedGuidanceRichContent: '<p>Keep the learner focused on the relationship between outdoor temperature and flow temperature demand.</p>',
          SuccessIndicatorRichContent: '<p>The learner references outdoor temperature while explaining the adjustment.</p>',
          SpotlightTarget: {
            selector: selectors[3]?.selector,
            position: 'left',
            displayName: selectors[3]?.name || 'Outdoor temperature',
          },
        },
      ],
    },
  ];

  return JSON.stringify(template, null, 2);
};

const buildAiInstructions = () => {
  const iconExamples = AVAILABLE_QUEST_ICONS.slice(0, 8).map((icon) => icon.key).join(', ');
  const selectorExamples = AVAILABLE_SPOTLIGHT_SELECTORS.slice(0, 6)
    .map((selector) => `${selector.name}: ${selector.selector}`)
    .join('\n');

  return [
    'Generate a JSON array of training-profile tutorial quests for a heat pump simulator.',
    'Each quest may include Title, Category, Difficulty, EstimatedTime, IconKey, IsChallenge, rich HTML content fields, optional ConditionAst, and a Steps array.',
    'Each step may include StepKey, TaskTitle, InstructionRichContent, DetailedGuidanceRichContent, SuccessIndicatorRichContent, and optional SpotlightTarget.',
    'Use valid JSON only, not markdown.',
    'Rich content fields should be concise HTML strings such as <p>...</p>.',
    'ConditionAst must use allowed fields and operators from the editor guide.',
    'SpotlightTarget should be an object with selector, position, optional displayName, and optional fallbackRect.',
    `Known IconKey examples: ${iconExamples}`,
    'Known spotlight selector examples:',
    selectorExamples,
  ].join('\n');
};

/**
 * Modal component used to bulk import training profile tutorial quests.
 * @param {object} props Component props.
 * @param {boolean} props.open Whether the modal is visible.
 * @param {number} props.profileId Target training profile identifier.
 * @param {Function} props.onClose Invoked when the modal should close.
 * @param {Function} props.onSuccess Invoked after a successful bulk import.
 * @returns {JSX.Element} Bulk import modal.
 */
const TrainingProfileBulkImportModal = ({
  open,
  profileId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);

  const [jsonValue, setJsonValue] = useState('');
  const [parsedQuests, setParsedQuests] = useState([]);
  const [normalizedQuests, setNormalizedQuests] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [importing, setImporting] = useState(false);
  const [selectedQuestIndex, setSelectedQuestIndex] = useState(null);
  const [editorLoadState, setEditorLoadState] = useState('idle');
  const [editorRenderKey, setEditorRenderKey] = useState(0);

  const aiInstructions = useMemo(() => buildAiInstructions(), []);

  const validCount = useMemo(
    () => validationResults.filter((result) => result.valid).length,
    [validationResults]
  );

  const errorCount = useMemo(
    () => validationResults.filter((result) => !result.valid).length,
    [validationResults]
  );

  const canImport = normalizedQuests.length > 0 && errorCount === 0 && !parseError;

  const resetEditorState = useCallback(() => {
    editorRef.current = null;
    setEditorLoadState('idle');
    setEditorRenderKey((currentKey) => currentKey + 1);
  }, []);

  useEffect(() => {
    const result = parseJson(jsonValue);

    if (!result.success) {
      setParseError(result.error);
      setParsedQuests([]);
      setNormalizedQuests([]);
      setValidationResults([]);
      return;
    }

    const nextNormalizedQuests = result.data.map((quest) => normalizeQuest(quest));
    const nextValidationResults = nextNormalizedQuests.map((quest, index) => {
      const errors = validateQuest(quest);
      const iconLabel = quest.iconKey ? (ICON_LABEL_MAP.get(quest.iconKey) || quest.iconKey) : null;

      return {
        index,
        title: quest.title || t('trainingProfiles.bulkImport.untitledQuest', 'Untitled quest'),
        category: quest.category || null,
        difficulty: quest.difficulty || null,
        estimatedTime: quest.estimatedTime || null,
        iconKey: quest.iconKey || null,
        iconLabel,
        iconKnown: quest.iconKey ? KNOWN_ICON_KEYS.has(quest.iconKey) : true,
        isChallenge: quest.isChallenge,
        stepCount: quest.steps.length,
        spotlightCount: quest.steps.filter((step) => step.spotlightTarget).length,
        conditionUsed: Boolean(quest.conditionAst),
        stepTitles: quest.steps.slice(0, 4).map((step) => step.taskTitle || t('trainingProfiles.bulkImport.untitledStep', 'Untitled step')),
        errors,
        valid: errors.length === 0,
      };
    });

    setParseError(null);
    setParsedQuests(result.data);
    setNormalizedQuests(nextNormalizedQuests);
    setValidationResults(nextValidationResults);
  }, [jsonValue, t]);

  useEffect(() => {
    if (!open) {
      setJsonValue('');
      setParsedQuests([]);
      setNormalizedQuests([]);
      setParseError(null);
      setValidationResults([]);
      setImporting(false);
      setSelectedQuestIndex(null);
      resetEditorState();
    }
  }, [open, resetEditorState]);

  useEffect(() => {
    let isActive = true;
    let timeoutId = null;

    if (!open || editorLoadState !== 'loading') {
      return undefined;
    }

    const initPromise = import('monaco-editor').then((monacoModule) => {
      const monaco = monacoModule.default ?? monacoModule;
      loader.config({ monaco });
      return loader.init();
    });

    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`Monaco initialization timed out after ${MONACO_INIT_TIMEOUT_MS}ms`));
      }, MONACO_INIT_TIMEOUT_MS);
    });

    Promise.race([initPromise, timeoutPromise])
      .then(() => {
        if (isActive) {
          setEditorLoadState('ready');
        }
      })
      .catch((error) => {
        console.error('Monaco initialization failed; using textarea fallback.', error);
        if (isActive) {
          setEditorLoadState('failed');
        }
      })
      .finally(() => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      });

    return () => {
      isActive = false;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [editorLoadState, open]);

  useEffect(() => {
    let timeoutId = null;

    if (!open || editorLoadState !== 'ready' || editorRef.current) {
      return undefined;
    }

    timeoutId = window.setTimeout(() => {
      if (!editorRef.current) {
        console.error('Monaco editor mount timed out; using textarea fallback.');
        setEditorLoadState('failed');
      }
    }, MONACO_MOUNT_TIMEOUT_MS);

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [editorLoadState, editorRenderKey, open]);

  const handleRetryEditor = useCallback(() => {
    editorRef.current = null;
    setEditorRenderKey((currentKey) => currentKey + 1);
    setEditorLoadState('loading');
  }, []);

  const handleEnableAdvancedEditor = useCallback(() => {
    if (editorLoadState === 'loading') {
      return;
    }

    editorRef.current = null;
    setEditorRenderKey((currentKey) => currentKey + 1);
    setEditorLoadState('loading');
  }, [editorLoadState]);

  const handleEditorDidMount = useCallback((editor) => {
    editorRef.current = editor;
  }, []);

  const copyToClipboard = useCallback(async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(successMessage);
    } catch (error) {
      console.error('Clipboard write failed:', error);
      message.error(t('trainingProfiles.bulkImport.copyError', 'Failed to copy to clipboard'));
    }
  }, [t]);

  const handleGenerateTemplate = useCallback(() => {
    setJsonValue(generateTemplateJson());
  }, []);

  const handleCopyTemplate = useCallback(() => {
    copyToClipboard(
      generateTemplateJson(),
      t('trainingProfiles.bulkImport.templateCopied', 'Template copied to clipboard')
    );
  }, [copyToClipboard, t]);

  const handleCopyAiInstructions = useCallback(() => {
    copyToClipboard(
      aiInstructions,
      t('trainingProfiles.bulkImport.aiInstructionsCopied', 'AI instructions copied to clipboard')
    );
  }, [aiInstructions, copyToClipboard, t]);

  const handleFormatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonValue);
      setJsonValue(JSON.stringify(parsed, null, 2));
    } catch {
      message.warning(t('trainingProfiles.bulkImport.cannotFormat', 'Cannot format invalid JSON'));
    }
  }, [jsonValue, t]);

  const handleClear = useCallback(() => {
    setJsonValue('');
    setSelectedQuestIndex(null);
  }, []);

  const handleLocateQuest = useCallback((questIndex) => {
    setSelectedQuestIndex(questIndex);

    if (!editorRef.current || !jsonValue) {
      return;
    }

    const lineRange = findQuestLineRange(jsonValue, questIndex);
    if (!lineRange) {
      return;
    }

    const editor = editorRef.current;
    editor.revealLineInCenter(lineRange.startLine);
    editor.setSelection({
      startLineNumber: lineRange.startLine,
      startColumn: 1,
      endLineNumber: lineRange.endLine,
      endColumn: 1000,
    });
    editor.focus();
  }, [jsonValue]);

  const handleImport = useCallback(async () => {
    if (!canImport || !profileId) {
      return;
    }

    setImporting(true);
    try {
      const requestQuests = normalizedQuests.map((quest, index) => mapQuestForRequest(quest, index));
      const result = await trainingProfilesService.bulkAddTrainingProfileQuests(profileId, requestQuests);

      if (result.success) {
        message.success(
          t(
            'trainingProfiles.bulkImport.success',
            'Imported {{questCount}} quests and {{stepCount}} steps',
            { questCount: result.successCount, stepCount: result.importedStepCount }
          )
        );
        onSuccess?.();
        onClose();
        return;
      }

      const errorMessages = result.errors
        ?.map((error) => `#${error.index + 1}: ${error.errors.join(', ')}`)
        .join('\n');

      Modal.error({
        title: t('trainingProfiles.bulkImport.importFailed', 'Import failed'),
        content: (
          <div className={styles.serverErrorBody}>
            {errorMessages || t('trainingProfiles.bulkImport.unknownError', 'Unknown import error')}
          </div>
        ),
        width: 680,
      });
    } catch (error) {
      console.error('Training profile bulk import error:', error);
      const serverErrors = error.response?.data?.errors;
      if (Array.isArray(serverErrors) && serverErrors.length > 0) {
        const errorMessages = serverErrors
          .map((item) => `#${item.index + 1}: ${item.errors.join(', ')}`)
          .join('\n');

        Modal.error({
          title: t('trainingProfiles.bulkImport.importFailed', 'Import failed'),
          content: <div className={styles.serverErrorBody}>{errorMessages}</div>,
          width: 680,
        });
      } else {
        const errorMessage = error.response?.data?.message
          || error.message
          || t('trainingProfiles.bulkImport.unknownError', 'Unknown import error');
        message.error(errorMessage);
      }
    } finally {
      setImporting(false);
    }
  }, [canImport, normalizedQuests, onClose, onSuccess, profileId, t]);

  const renderEditor = () => {
    if (editorLoadState === 'failed') {
      return (
        <div className={styles.fallbackEditorLayout}>
          <Alert
            type="warning"
            showIcon
            message={t('trainingProfiles.bulkImport.editorFallbackTitle', 'Advanced editor unavailable')}
            description={t(
              'trainingProfiles.bulkImport.editorFallbackDescription',
              'The syntax-highlighting editor could not be loaded. You can still paste and import JSON using the plain text editor below.'
            )}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryEditor}>
                {t('trainingProfiles.bulkImport.retryEditor', 'Retry editor')}
              </Button>
            }
          />
          <TextArea
            value={jsonValue}
            onChange={(event) => setJsonValue(event.target.value)}
            spellCheck={false}
            className={styles.fallbackTextarea}
          />
        </div>
      );
    }

    if (editorLoadState !== 'ready') {
      return (
        <div className={styles.fallbackEditorLayout}>
          <Alert
            type="info"
            showIcon
            message={
              editorLoadState === 'loading'
                ? t('trainingProfiles.bulkImport.loadingEditorTitle', 'Loading advanced editor')
                : t('trainingProfiles.bulkImport.basicEditorTitle', 'Plain text editor')
            }
            description={t(
              editorLoadState === 'loading'
                ? 'trainingProfiles.bulkImport.loadingEditorDescription'
                : 'trainingProfiles.bulkImport.basicEditorDescription',
              editorLoadState === 'loading'
                ? 'You can keep editing while the Monaco editor initializes in the background.'
                : 'The plain text editor is ready now. Start the advanced editor if you want syntax highlighting and code navigation.'
            )}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleEnableAdvancedEditor}
                loading={editorLoadState === 'loading'}
              >
                {editorLoadState === 'loading'
                  ? t('trainingProfiles.bulkImport.loadingEditorButton', 'Loading...')
                  : t('trainingProfiles.bulkImport.enableEditor', 'Use advanced editor')}
              </Button>
            }
          />
          <TextArea
            value={jsonValue}
            onChange={(event) => setJsonValue(event.target.value)}
            spellCheck={false}
            className={styles.fallbackTextarea}
          />
        </div>
      );
    }

    return (
      <Editor
        key={editorRenderKey}
        height="100%"
        defaultLanguage="json"
        value={jsonValue}
        onChange={(value) => setJsonValue(value ?? '')}
        onMount={handleEditorDidMount}
        theme="vs-light"
        options={{
          minimap: { enabled: false },
          fontSize: 13,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on',
          folding: true,
          bracketPairColorization: { enabled: true },
        }}
      />
    );
  };

  const renderQuestCard = useCallback((item) => {
    const isSelected = selectedQuestIndex === item.index;

    return (
      <div
        key={item.index}
        className={[
          styles.questCard,
          item.valid ? styles.questCardValid : styles.questCardInvalid,
          isSelected ? styles.questCardSelected : '',
        ].filter(Boolean).join(' ')}
        onClick={() => handleLocateQuest(item.index)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleLocateQuest(item.index);
          }
        }}
      >
        <div className={styles.questCardHeader}>
          <div className={styles.questCardTitleRow}>
            <Badge count={item.index + 1} style={{ backgroundColor: item.valid ? '#52c41a' : '#ff4d4f' }} />
            <Text strong>{item.title}</Text>
          </div>
          <Space size={4}>
            <Tooltip title={t('trainingProfiles.bulkImport.locateInEditor', 'Locate in editor')}>
              <Button
                type="text"
                size="small"
                icon={<AimOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  handleLocateQuest(item.index);
                }}
              />
            </Tooltip>
            {item.valid ? (
              <CheckCircleOutlined className={styles.questCardStatusOk} />
            ) : (
              <CloseCircleOutlined className={styles.questCardStatusError} />
            )}
          </Space>
        </div>

        <div className={styles.questCardMeta}>
          {item.category && <Tag color="blue">{item.category}</Tag>}
          {item.difficulty && <Tag color="purple">{item.difficulty}</Tag>}
          {item.estimatedTime && <Tag color="cyan">{item.estimatedTime}</Tag>}
          {item.isChallenge && <Tag color="volcano">{t('trainingProfiles.bulkImport.challenge', 'Challenge')}</Tag>}
        </div>

        <div className={styles.questCardStats}>
          <span>{item.stepCount} {t('trainingProfiles.bulkImport.steps', 'steps')}</span>
          <span>{item.spotlightCount} {t('trainingProfiles.bulkImport.spotlights', 'spotlights')}</span>
          <span>{item.conditionUsed ? t('trainingProfiles.bulkImport.conditionConfigured', 'condition set') : t('trainingProfiles.bulkImport.noCondition', 'no condition')}</span>
        </div>

        {item.iconKey && (
          <div className={styles.questCardIconRow}>
            <Text type="secondary">{t('trainingProfiles.bulkImport.icon', 'Icon')}:</Text>
            <Tag color={item.iconKnown ? 'default' : 'warning'}>{item.iconLabel}</Tag>
          </div>
        )}

        {item.stepTitles.length > 0 ? (
          <div className={styles.questCardSteps}>
            <Text type="secondary">{t('trainingProfiles.bulkImport.stepPreview', 'Step preview')}</Text>
            <ul className={styles.questCardStepList}>
              {item.stepTitles.map((stepTitle, stepIndex) => (
                <li key={`${item.index}-${stepIndex}`}>{stepTitle}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className={styles.questCardStepsEmpty}>
            {t('trainingProfiles.bulkImport.noStepsDefined', 'No steps defined yet')}
          </div>
        )}

        {!item.valid && item.errors.length > 0 && (
          <div className={styles.questCardErrors}>
            <Text type="danger" strong>{t('trainingProfiles.bulkImport.validationIssues', 'Validation issues')}</Text>
            <ul className={styles.questErrorList}>
              {item.errors.map((error, errorIndex) => (
                <li key={`${item.index}-error-${errorIndex}`}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }, [handleLocateQuest, selectedQuestIndex, t]);

  return (
    <Modal
      title={
        <Space>
          <DownloadOutlined />
          {t('trainingProfiles.bulkImport.title', 'Bulk import tutorial quests')}
        </Space>
      }
      open={open}
      onCancel={onClose}
      width="95vw"
      style={{ top: 20, maxWidth: 1640 }}
      styles={{ body: { padding: '16px 24px', height: 'calc(100vh - 160px)', overflow: 'hidden' } }}
      footer={
        <div className={styles.modalFooter}>
          <Space>
            {normalizedQuests.length > 0 && (
              <>
                <Badge count={validCount} style={{ backgroundColor: '#52c41a' }} showZero />
                <Text type="secondary">{t('trainingProfiles.bulkImport.valid', 'valid')}</Text>
                {errorCount > 0 && (
                  <>
                    <Badge count={errorCount} style={{ backgroundColor: '#ff4d4f' }} />
                    <Text type="secondary">{t('trainingProfiles.bulkImport.withErrors', 'with errors')}</Text>
                  </>
                )}
              </>
            )}
          </Space>
          <Space>
            <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleImport}
              disabled={!canImport}
              loading={importing}
            >
              {importing
                ? t('trainingProfiles.bulkImport.importing', 'Importing...')
                : t('trainingProfiles.bulkImport.importButton', 'Import {{count}} quests', { count: normalizedQuests.length })}
            </Button>
          </Space>
        </div>
      }
    >
      <div className={styles.bulkImportLayout}>
        <div className={styles.bulkImportEditor}>
          <div className={styles.bulkImportToolbar}>
            <Space size="small" wrap>
              <Tooltip title={t('trainingProfiles.bulkImport.templateTooltip', 'Insert an example JSON template')}>
                <Button icon={<DownloadOutlined />} onClick={handleGenerateTemplate}>
                  {t('trainingProfiles.bulkImport.template', 'Template')}
                </Button>
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.copyTemplateTooltip', 'Copy template JSON for AI tools')}>
                <Button icon={<CopyOutlined />} onClick={handleCopyTemplate} />
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.copyAiInstructionsTooltip', 'Copy AI instructions')}>
                <Button icon={<BulbOutlined />} onClick={handleCopyAiInstructions} />
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.formatTooltip', 'Format JSON')}>
                <Button icon={<FormatPainterOutlined />} onClick={handleFormatJson} disabled={!jsonValue} />
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.clearTooltip', 'Clear editor')}>
                <Button icon={<ClearOutlined />} onClick={handleClear} disabled={!jsonValue} />
              </Tooltip>
            </Space>
            <Text type="secondary" className={styles.characterCount}>
              {jsonValue.length.toLocaleString()} {t('trainingProfiles.bulkImport.chars', 'chars')}
            </Text>
          </div>

          <div className={styles.bulkImportEditorWrapper}>
            {renderEditor()}
          </div>

          {parseError ? (
            <Alert
              type="error"
              showIcon
              message={t('trainingProfiles.bulkImport.invalidJson', 'Invalid JSON')}
              description={parseError}
              className={styles.bulkImportStatus}
            />
          ) : normalizedQuests.length > 0 ? (
            <Alert
              type={errorCount > 0 ? 'warning' : 'success'}
              showIcon
              message={
                errorCount > 0
                  ? t('trainingProfiles.bulkImport.validationErrors', '{{count}} quest(s) have errors', { count: errorCount })
                  : t('trainingProfiles.bulkImport.allValid', 'All quests are valid')
              }
              description={t(
                'trainingProfiles.bulkImport.readyCount',
                '{{questCount}} quest(s) with {{stepCount}} steps parsed',
                {
                  questCount: normalizedQuests.length,
                  stepCount: normalizedQuests.reduce((total, quest) => total + quest.steps.length, 0),
                }
              )}
              className={styles.bulkImportStatus}
            />
          ) : null}
        </div>

        <div className={styles.bulkImportPreview}>
          <Collapse
            ghost
            size="small"
            defaultActiveKey={['guide', 'ai']}
            items={[
              {
                key: 'guide',
                label: (
                  <Space>
                    <CodeOutlined />
                    <Text strong>{t('trainingProfiles.bulkImport.schemaTitle', 'Schema guide')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.schemaCard}>
                    <div className={styles.schemaGrid}>
                      <div><Text code>Title</Text></div>
                      <div>{t('trainingProfiles.bulkImport.schemaTitleField', 'Required. Max 255 chars.')}</div>
                      <div><Text code>Category</Text>, <Text code>Difficulty</Text>, <Text code>EstimatedTime</Text></div>
                      <div>{t('trainingProfiles.bulkImport.schemaMetaFields', 'Optional quest metadata labels.')}</div>
                      <div><Text code>IconKey</Text></div>
                      <div>{t('trainingProfiles.bulkImport.schemaIcon', 'Optional frontend icon key from the known icon list.')}</div>
                      <div><Text code>ConditionAst</Text></div>
                      <div>{t('trainingProfiles.bulkImport.schemaCondition', 'Optional condition AST JSON evaluated against simulator state.')}</div>
                      <div><Text code>Steps</Text></div>
                      <div>{t('trainingProfiles.bulkImport.schemaSteps', 'Array of step objects with TaskTitle and optional spotlight metadata.')}</div>
                      <div><Text code>SpotlightTarget</Text></div>
                      <div>{t('trainingProfiles.bulkImport.schemaSpotlight', 'Optional object with selector, position, displayName, and fallbackRect.')}</div>
                    </div>

                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.iconExamples', 'Known icon keys')}</Text>
                      <div className={styles.tagWrap}>
                        {AVAILABLE_QUEST_ICONS.slice(0, 18).map((icon) => (
                          <Tag key={icon.key}>{icon.key}</Tag>
                        ))}
                      </div>
                    </div>

                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.selectorExamples', 'Spotlight selector examples')}</Text>
                      <div className={styles.selectorList}>
                        {AVAILABLE_SPOTLIGHT_SELECTORS.slice(0, 10).map((selector) => (
                          <div key={selector.selector} className={styles.selectorRow}>
                            <strong>{selector.name}</strong>
                            <code>{selector.selector}</code>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'ai',
                label: (
                  <Space>
                    <BulbOutlined />
                    <Text strong>{t('trainingProfiles.bulkImport.aiTitle', 'AI instructions')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.aiCard}>
                    <Paragraph className={styles.aiLead}>
                      {t('trainingProfiles.bulkImport.aiLead', 'Use this prompt with your AI tool, then paste the returned JSON into the editor.')}
                    </Paragraph>
                    <pre className={styles.aiPrompt}>{aiInstructions}</pre>
                    <div className={styles.aiActions}>
                      <Button icon={<CopyOutlined />} onClick={handleCopyAiInstructions}>
                        {t('trainingProfiles.bulkImport.copyAiInstructions', 'Copy AI instructions')}
                      </Button>
                    </div>
                    <Alert
                      type="info"
                      showIcon
                      icon={<InfoCircleOutlined />}
                      message={t('trainingProfiles.bulkImport.aiTipTitle', 'Authoring tip')}
                      description={t(
                        'trainingProfiles.bulkImport.aiTipBody',
                        'Ask the AI to keep HTML minimal, use only valid JSON, and avoid inventing spotlight selectors beyond the provided examples.'
                      )}
                    />
                  </div>
                ),
              },
            ]}
          />

          {validationResults.length > 0 ? (
            <div className={styles.questListSection}>
              <div className={styles.previewHeader}>
                <Space>
                  <AimOutlined />
                  <Text strong>{t('trainingProfiles.bulkImport.previewTitle', 'Quest preview')}</Text>
                </Space>
                <Text type="secondary">{t('trainingProfiles.bulkImport.previewHint', 'Click a card to locate it in the editor')}</Text>
              </div>
              <div className={styles.questCardList}>
                {validationResults.map((item) => renderQuestCard(item))}
              </div>
            </div>
          ) : !parseError ? (
            <div className={styles.bulkEmptyState}>
              <DownloadOutlined className={styles.bulkEmptyIcon} />
              <Text type="secondary">{t('trainingProfiles.bulkImport.emptyState', 'Paste JSON or insert the template to begin')}</Text>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default TrainingProfileBulkImportModal;